import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { PageHeader, Card, CardBody } from "../../components/ui";
import {
  FaUsers, FaArrowRight, FaArrowLeft, FaChevronRight, FaCheckCircle,
  FaExclamationTriangle, FaLightbulb, FaList, FaColumns, FaUpload, FaUserPlus,
  FaWindowMaximize, FaCheckSquare, FaFilter, FaCodeBranch, FaTag, FaPhone,
  FaEnvelope, FaFileAlt, FaGripVertical, FaSearch, FaTrash
} from "react-icons/fa";

export default function CRMGuide() {
  useEffect(() => {
    document.title = "CRM & Lead Management Guide | Walytic - Track WhatsApp Leads";
  }, []);

  return (
    <div className="page-content">
      <PageHeader title="CRM & Lead Management Guide" subtitle="Track every WhatsApp lead from first contact to conversion. Organize with stages, tags, and custom fields." />

      <div className="max-w-4xl mx-auto">

        {/* Table of Contents */}
        <nav className="bg-surface-card rounded-xl border border-surface-border p-6 mb-12">
          <h2 className="font-semibold text-lg text-ink-primary mb-4">In this guide</h2>
          <div className="grid sm:grid-cols-2 gap-2">
            {[
              { id: 'pipeline-stages', label: 'Pipeline stages' },
              { id: 'two-views', label: 'List view & Kanban view' },
              { id: 'adding-a-lead', label: 'Adding a lead' },
              { id: 'import-csv', label: 'Import from CSV' },
              { id: 'promote-contacts', label: 'Promoting contacts to leads' },
              { id: 'lead-detail-panel', label: 'Lead detail panel' },
              { id: 'bulk-actions', label: 'Bulk actions' },
              { id: 'filters', label: 'Filters' },
              { id: 'assigning-flows', label: 'Assigning flows' },
            ].map((item) => (
              <a key={item.id} href={`#${item.id}`} className="flex items-center text-sm text-ink-secondary hover:text-brand py-1.5 transition-colors">
                <FaChevronRight className="h-4 w-4 mr-1 text-ink-muted" />
                {item.label}
              </a>
            ))}
          </div>
        </nav>

        {/* Section: Pipeline Stages */}
        <section id="pipeline-stages" className="mb-16 scroll-mt-24">
          <h2 className="text-3xl font-bold text-ink-primary mb-4">Pipeline stages</h2>
          <p className="text-ink-secondary mb-6 leading-relaxed">
            Every lead in Walytic sits in one of five pipeline stages. Stages move left to right,
            from first contact through to outcome. You can change a lead&apos;s stage at any time from
            the detail panel or through bulk actions.
          </p>

          <div className="flex flex-wrap items-center gap-3 mb-6">
            {[
              { name: 'New', color: 'bg-blue-100 text-blue-700 border-blue-200' },
              { name: 'Contacted', color: 'bg-amber-100 text-amber-700 border-amber-200' },
              { name: 'Qualified', color: 'bg-purple-100 text-purple-700 border-purple-200' },
              { name: 'Converted', color: 'bg-green-100 text-green-700 border-green-200' },
              { name: 'Lost', color: 'bg-red-100 text-red-700 border-red-200' },
            ].map((stage, i, arr) => (
              <div key={stage.name} className="flex items-center gap-3">
                <span className={`px-4 py-2 rounded-lg border text-sm font-semibold ${stage.color}`}>
                  {stage.name}
                </span>
                {i < arr.length - 1 && (
                  <FaArrowRight className="h-4 w-4 text-ink-muted" />
                )}
              </div>
            ))}
          </div>

          <div className="bg-surface-card border border-surface-border rounded-xl overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b border-surface-border">
                <tr>
                  <th className="text-left py-3 px-4 font-semibold text-ink-primary">Stage</th>
                  <th className="text-left py-3 px-4 font-semibold text-ink-primary">When to use</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {[
                  ['New', 'Lead just entered the CRM. No outreach yet.'],
                  ['Contacted', 'You sent the first message or the lead replied to a campaign.'],
                  ['Qualified', 'The lead showed real interest or meets your criteria.'],
                  ['Converted', 'Deal closed. The lead became a paying customer.'],
                  ['Lost', 'Lead went cold, unsubscribed, or chose a competitor.'],
                ].map(([stage, desc]) => (
                  <tr key={stage}>
                    <td className="py-3 px-4 font-medium text-ink-primary">{stage}</td>
                    <td className="py-3 px-4 text-ink-secondary">{desc}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        {/* Section: Two Views */}
        <section id="two-views" className="mb-16 scroll-mt-24">
          <h2 className="text-3xl font-bold text-ink-primary mb-4">List view &amp; Kanban view</h2>
          <p className="text-ink-secondary mb-6 leading-relaxed">
            Walytic gives you two ways to look at your leads. Switch between them using the toggle at the top of the CRM page.
          </p>

          <div className="grid sm:grid-cols-2 gap-6">
            <div className="bg-surface-card border border-surface-border rounded-xl p-6">
              <div className="flex items-center gap-3 mb-3">
                <div className="h-8 w-8 rounded-lg bg-slate-100 flex items-center justify-center">
                  <FaList className="h-4 w-4 text-ink-secondary" />
                </div>
                <h3 className="font-semibold text-lg text-ink-primary">List view</h3>
              </div>
              <p className="text-ink-secondary text-sm mb-4">
                A table with sortable columns. Best for scanning many leads quickly and performing bulk actions.
              </p>
              <p className="text-xs font-medium text-ink-muted mb-2">Columns:</p>
              <div className="flex flex-wrap gap-1.5">
                {['Name', 'Phone', 'Email', 'Stage', 'Source', 'Tags', 'Created'].map((col) => (
                  <span key={col} className="text-xs px-2 py-1 rounded bg-slate-100 text-ink-secondary">{col}</span>
                ))}
              </div>
            </div>

            <div className="bg-surface-card border border-surface-border rounded-xl p-6">
              <div className="flex items-center gap-3 mb-3">
                <div className="h-8 w-8 rounded-lg bg-indigo-100 flex items-center justify-center">
                  <FaColumns className="h-4 w-4 text-indigo-600" />
                </div>
                <h3 className="font-semibold text-lg text-ink-primary">Kanban view</h3>
              </div>
              <p className="text-ink-secondary text-sm mb-4">
                Five columns, one per stage. Drag and drop lead cards between columns to change their stage instantly.
              </p>
              <div className="flex items-center gap-2 text-xs text-ink-muted">
                <FaGripVertical className="h-4 w-4" />
                <span>Drag a card from &quot;New&quot; to &quot;Contacted&quot; after your first message</span>
              </div>
            </div>
          </div>
        </section>

        {/* Section: Adding a Lead */}
        <section id="adding-a-lead" className="mb-16 scroll-mt-24">
          <h2 className="text-3xl font-bold text-ink-primary mb-4">Adding a lead</h2>
          <p className="text-ink-secondary mb-6 leading-relaxed">
            Click the <strong>&quot;Add Lead&quot;</strong> button at the top of the CRM page. A form opens with these fields:
          </p>

          <div className="bg-surface-card border border-surface-border rounded-xl overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b border-surface-border">
                <tr>
                  <th className="text-left py-3 px-4 font-semibold text-ink-primary">Field</th>
                  <th className="text-left py-3 px-4 font-semibold text-ink-primary">Required?</th>
                  <th className="text-left py-3 px-4 font-semibold text-ink-primary">Details</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {[
                  ['Phone', 'Yes', '7-15 digits. Include country code (e.g., 919876543210).'],
                  ['First Name', 'No', 'Used in message variables like {{firstName}}.'],
                  ['Last Name', 'No', 'Combined with first name for the full name display.'],
                  ['Email', 'No', 'Optional contact email for the lead record.'],
                  ['Stage', 'No', 'Defaults to "New" if not selected.'],
                  ['Tags', 'No', 'Comma-separated. Example: hot-lead, webinar-signup.'],
                  ['Notes', 'No', 'Free-text field for any context about the lead.'],
                ].map(([field, req, detail]) => (
                  <tr key={field}>
                    <td className="py-3 px-4 font-medium text-ink-primary">{field}</td>
                    <td className="py-3 px-4">
                      {req === 'Yes' ? (
                        <span className="text-xs px-2 py-0.5 rounded bg-red-100 text-red-700">Required</span>
                      ) : (
                        <span className="text-xs px-2 py-0.5 rounded bg-slate-100 text-ink-muted">Optional</span>
                      )}
                    </td>
                    <td className="py-3 px-4 text-ink-secondary">{detail}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        {/* Section: Import from CSV */}
        <section id="import-csv" className="mb-16 scroll-mt-24">
          <h2 className="text-3xl font-bold text-ink-primary mb-4">Import from CSV</h2>
          <p className="text-ink-secondary mb-6 leading-relaxed">
            Got a spreadsheet of leads? Import them in bulk instead of adding one by one.
          </p>

          <div className="space-y-8">
            <div className="flex gap-4">
              <div className="flex-shrink-0 h-8 w-8 rounded-full bg-brand text-white flex items-center justify-center text-sm font-bold">1</div>
              <div>
                <h3 className="font-semibold text-lg text-ink-primary mb-2">Open the import dialog</h3>
                <p className="text-ink-secondary">
                  Click <strong>&quot;Import CSV&quot;</strong> at the top of the CRM page.
                </p>
              </div>
            </div>

            <div className="flex gap-4">
              <div className="flex-shrink-0 h-8 w-8 rounded-full bg-brand text-white flex items-center justify-center text-sm font-bold">2</div>
              <div>
                <h3 className="font-semibold text-lg text-ink-primary mb-2">Drag and drop your file</h3>
                <p className="text-ink-secondary mb-3">
                  Drop a <code className="bg-slate-100 px-1.5 py-0.5 rounded text-sm">.csv</code> or <code className="bg-slate-100 px-1.5 py-0.5 rounded text-sm">.txt</code> file
                  into the upload area. Walytic shows a 5-row preview so you can verify the data looks correct.
                </p>
              </div>
            </div>

            <div className="flex gap-4">
              <div className="flex-shrink-0 h-8 w-8 rounded-full bg-brand text-white flex items-center justify-center text-sm font-bold">3</div>
              <div>
                <h3 className="font-semibold text-lg text-ink-primary mb-2">Match your columns</h3>
                <p className="text-ink-secondary mb-3">
                  The expected columns are:
                </p>
                <div className="flex flex-wrap gap-2 mb-3">
                  <span className="text-xs px-2.5 py-1 rounded bg-red-50 text-red-700 border border-red-200 font-medium">phone (required)</span>
                  <span className="text-xs px-2.5 py-1 rounded bg-slate-100 text-ink-secondary">name</span>
                  <span className="text-xs px-2.5 py-1 rounded bg-slate-100 text-ink-secondary">email</span>
                  <span className="text-xs px-2.5 py-1 rounded bg-slate-100 text-ink-secondary">tags</span>
                </div>
              </div>
            </div>

            <div className="flex gap-4">
              <div className="flex-shrink-0 h-8 w-8 rounded-full bg-brand text-white flex items-center justify-center text-sm font-bold">4</div>
              <div>
                <h3 className="font-semibold text-lg text-ink-primary mb-2">Review the results</h3>
                <p className="text-ink-secondary mb-3">
                  After import, Walytic shows a summary with three counts:
                </p>
                <div className="flex gap-3">
                  <span className="text-xs px-3 py-1.5 rounded-lg bg-green-50 text-green-700 border border-green-200 font-medium">Created</span>
                  <span className="text-xs px-3 py-1.5 rounded-lg bg-blue-50 text-blue-700 border border-blue-200 font-medium">Updated</span>
                  <span className="text-xs px-3 py-1.5 rounded-lg bg-slate-100 text-ink-secondary border border-surface-border font-medium">Skipped</span>
                </div>
              </div>
            </div>
          </div>

          <div className="mt-8 bg-blue-50 border border-blue-100 rounded-lg p-4">
            <p className="text-sm text-blue-800">
              <strong>Tip:</strong> If a phone number already exists in your CRM, the import updates that lead instead of creating a duplicate.
              Rows with missing or invalid phone numbers are skipped.
            </p>
          </div>
        </section>

        {/* Section: Promoting Contacts to Leads */}
        <section id="promote-contacts" className="mb-16 scroll-mt-24">
          <h2 className="text-3xl font-bold text-ink-primary mb-4">Promoting contacts to leads</h2>
          <p className="text-ink-secondary mb-6 leading-relaxed">
            Not every WhatsApp contact is a lead right away. When a conversation in your Unibox inbox looks promising,
            you can promote that contact into the CRM with one click.
          </p>

          <div className="space-y-8">
            <div className="flex gap-4">
              <div className="flex-shrink-0 h-8 w-8 rounded-full bg-indigo-600 text-white flex items-center justify-center text-sm font-bold">1</div>
              <div>
                <h3 className="font-semibold text-lg text-ink-primary mb-2">Open Unibox</h3>
                <p className="text-ink-secondary">
                  Go to <strong>Unibox</strong> in the left sidebar and open any conversation.
                </p>
              </div>
            </div>

            <div className="flex gap-4">
              <div className="flex-shrink-0 h-8 w-8 rounded-full bg-indigo-600 text-white flex items-center justify-center text-sm font-bold">2</div>
              <div>
                <h3 className="font-semibold text-lg text-ink-primary mb-2">Click &quot;Add Lead&quot;</h3>
                <p className="text-ink-secondary">
                  In the contact&apos;s info panel, click <strong>&quot;Add Lead&quot;</strong>. This creates a new CRM record
                  with the contact&apos;s phone number, name, and any existing tags already filled in.
                </p>
              </div>
            </div>

            <div className="flex gap-4">
              <div className="flex-shrink-0 h-8 w-8 rounded-full bg-indigo-600 text-white flex items-center justify-center text-sm font-bold">3</div>
              <div>
                <h3 className="font-semibold text-lg text-ink-primary mb-2">Lead appears in CRM</h3>
                <p className="text-ink-secondary">
                  The new lead starts in the <strong>New</strong> stage. Head over to the CRM page to set the stage, add notes, or assign a flow.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Section: Lead Detail Panel */}
        <section id="lead-detail-panel" className="mb-16 scroll-mt-24">
          <h2 className="text-3xl font-bold text-ink-primary mb-4">Lead detail panel</h2>
          <p className="text-ink-secondary mb-6 leading-relaxed">
            Click any lead row (List view) or card (Kanban view) to open the detail panel on the right side of the screen.
            The panel is 420px wide and contains everything about that lead.
          </p>

          <div className="space-y-4">
            {[
              {
                icon: <FaUserPlus className="h-4 w-4 text-blue-600" />,
                bg: 'bg-blue-100',
                title: 'Name & Email',
                desc: 'Editable fields. Click to update the first name, last name, or email directly.',
              },
              {
                icon: <FaPhone className="h-4 w-4 text-ink-secondary" />,
                bg: 'bg-slate-100',
                title: 'Phone number',
                desc: 'Read-only. This is the unique identifier for the lead and cannot be changed.',
              },
              {
                icon: <FaTag className="h-4 w-4 text-amber-600" />,
                bg: 'bg-amber-100',
                title: 'Source badge',
                desc: 'Shows where the lead came from: manual entry, CSV import, Unibox, or a flow.',
              },
              {
                icon: <FaFilter className="h-4 w-4 text-purple-600" />,
                bg: 'bg-purple-100',
                title: 'Stage dropdown',
                desc: 'Pick any of the five stages. The change saves instantly.',
              },
              {
                icon: <FaTag className="h-4 w-4 text-green-600" />,
                bg: 'bg-green-100',
                title: 'Tag pills',
                desc: 'Colored pills showing all assigned tags. Click the "+" to add more or "x" to remove.',
              },
              {
                icon: <FaFileAlt className="h-4 w-4 text-teal-600" />,
                bg: 'bg-teal-100',
                title: 'Custom fields',
                desc: 'Key-value pairs for any extra data. Add fields like "company", "deal-size", or "interest".',
              },
              {
                icon: <FaFileAlt className="h-4 w-4 text-orange-600" />,
                bg: 'bg-orange-100',
                title: 'Notes',
                desc: 'A free-text area. Jot down call summaries, follow-up reminders, or any context.',
              },
              {
                icon: <FaCodeBranch className="h-4 w-4 text-indigo-600" />,
                bg: 'bg-indigo-100',
                title: 'Flow assignment',
                desc: 'Dropdown listing all flows from Flow Builder. Assign a flow to auto-engage this lead.',
              },
              {
                icon: <FaList className="h-4 w-4 text-cyan-600" />,
                bg: 'bg-cyan-100',
                title: 'Activity timeline',
                desc: 'Chronological log of all events: stage changes, messages sent, flow executions, and edits.',
              },
            ].map((item) => (
              <div key={item.title} className="bg-surface-card rounded-xl border border-surface-border p-4 flex items-start gap-4">
                <div className={`h-8 w-8 rounded-lg ${item.bg} flex items-center justify-center flex-shrink-0`}>
                  {item.icon}
                </div>
                <div>
                  <h3 className="font-semibold text-sm text-ink-primary mb-1">{item.title}</h3>
                  <p className="text-sm text-ink-secondary">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Section: Bulk Actions */}
        <section id="bulk-actions" className="mb-16 scroll-mt-24">
          <h2 className="text-3xl font-bold text-ink-primary mb-4">Bulk actions</h2>
          <p className="text-ink-secondary mb-6 leading-relaxed">
            Need to update 50 leads at once? Use checkboxes in List view to select multiple leads,
            then pick an action from the toolbar that appears.
          </p>

          <div className="grid sm:grid-cols-2 gap-4">
            <div className="bg-surface-card border border-surface-border rounded-xl p-5">
              <div className="flex items-center gap-3 mb-3">
                <div className="h-8 w-8 rounded-lg bg-purple-100 flex items-center justify-center">
                  <FaCheckSquare className="h-4 w-4 text-purple-600" />
                </div>
                <h3 className="font-semibold text-ink-primary">Change Stage</h3>
              </div>
              <p className="text-sm text-ink-secondary">
                Select leads, click &quot;Change Stage&quot;, and pick the target stage. All selected leads move at once.
              </p>
            </div>

            <div className="bg-surface-card border border-surface-border rounded-xl p-5">
              <div className="flex items-center gap-3 mb-3">
                <div className="h-8 w-8 rounded-lg bg-red-100 flex items-center justify-center">
                  <FaTrash className="h-4 w-4 text-red-600" />
                </div>
                <h3 className="font-semibold text-ink-primary">Delete Selected</h3>
              </div>
              <p className="text-sm text-ink-secondary">
                Permanently removes the selected leads. A confirmation dialog appears before deletion.
              </p>
            </div>
          </div>

          <div className="mt-6 bg-amber-50 border border-amber-200 rounded-lg p-4">
            <div className="flex items-start gap-2">
              <FaExclamationTriangle className="h-5 w-5 text-amber-600 mt-0.5 flex-shrink-0" />
              <p className="text-sm text-amber-800">
                Deleting leads is permanent. Export your data first if you might need it later.
              </p>
            </div>
          </div>
        </section>

        {/* Section: Filters */}
        <section id="filters" className="mb-16 scroll-mt-24">
          <h2 className="text-3xl font-bold text-ink-primary mb-4">Filters</h2>
          <p className="text-ink-secondary mb-6 leading-relaxed">
            Two ways to narrow down your lead list:
          </p>

          <div className="space-y-4">
            <div className="bg-surface-card border border-surface-border rounded-xl p-5 flex items-start gap-4">
              <div className="h-8 w-8 rounded-lg bg-blue-100 flex items-center justify-center flex-shrink-0">
                <FaFilter className="h-4 w-4 text-blue-600" />
              </div>
              <div>
                <h3 className="font-semibold text-sm text-ink-primary mb-1">Stage dropdown</h3>
                <p className="text-sm text-ink-secondary">
                  Filter by one specific stage (New, Contacted, Qualified, Converted, or Lost) or view all stages at once.
                </p>
              </div>
            </div>

            <div className="bg-surface-card border border-surface-border rounded-xl p-5 flex items-start gap-4">
              <div className="h-8 w-8 rounded-lg bg-green-100 flex items-center justify-center flex-shrink-0">
                <FaSearch className="h-4 w-4 text-green-600" />
              </div>
              <div>
                <h3 className="font-semibold text-sm text-ink-primary mb-1">Search bar</h3>
                <p className="text-sm text-ink-secondary">
                  Type to search by name, phone number, or email. Results filter in real time as you type.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Section: Assigning Flows */}
        <section id="assigning-flows" className="mb-16 scroll-mt-24">
          <h2 className="text-3xl font-bold text-ink-primary mb-4">Assigning flows</h2>
          <p className="text-ink-secondary mb-6 leading-relaxed">
            You can connect any lead to an automation from Flow Builder. This is useful for triggering
            follow-up sequences, drip campaigns, or onboarding messages for a specific lead.
          </p>

          <div className="space-y-8">
            <div className="flex gap-4">
              <div className="flex-shrink-0 h-8 w-8 rounded-full bg-green-600 text-white flex items-center justify-center text-sm font-bold">1</div>
              <div>
                <h3 className="font-semibold text-lg text-ink-primary mb-2">Open the lead detail panel</h3>
                <p className="text-ink-secondary">
                  Click any lead to open the side panel.
                </p>
              </div>
            </div>

            <div className="flex gap-4">
              <div className="flex-shrink-0 h-8 w-8 rounded-full bg-green-600 text-white flex items-center justify-center text-sm font-bold">2</div>
              <div>
                <h3 className="font-semibold text-lg text-ink-primary mb-2">Find the flow assignment dropdown</h3>
                <p className="text-ink-secondary">
                  Scroll to the <strong>Assigned Flow</strong> section. The dropdown lists every flow you have
                  created in Flow Builder. Only active flows are shown.
                </p>
              </div>
            </div>

            <div className="flex gap-4">
              <div className="flex-shrink-0 h-8 w-8 rounded-full bg-green-600 text-white flex items-center justify-center text-sm font-bold">3</div>
              <div>
                <h3 className="font-semibold text-lg text-ink-primary mb-2">Select a flow</h3>
                <p className="text-ink-secondary">
                  Pick a flow and the assignment saves immediately. The flow will execute for this lead
                  based on its trigger rules (incoming message, schedule, or manual run).
                </p>
              </div>
            </div>
          </div>

          <div className="mt-8 bg-blue-50 border border-blue-100 rounded-lg p-4">
            <div className="flex items-start gap-2">
              <FaLightbulb className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
              <p className="text-sm text-blue-800">
                <strong>Combine CRM + Flow Builder:</strong> Import leads via CSV, assign a cold outreach flow,
                and let Walytic send personalized messages on autopilot. Track progress by watching leads move
                from &quot;New&quot; to &quot;Contacted&quot; to &quot;Qualified&quot;.
              </p>
            </div>
          </div>
        </section>

        {/* Prev / Next nav */}
        <div className="flex items-center justify-between border-t border-surface-border pt-8">
          <Link to="/guide/campaigns" className="text-sm text-ink-muted hover:text-ink-primary flex items-center gap-1">
            <FaArrowLeft className="h-4 w-4" /> Prev: Campaigns
          </Link>
          <Link to="/guide/send-via-excel" className="text-sm font-medium text-brand hover:opacity-80 flex items-center gap-1">
            Next: Send via Excel <FaArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </div>
    </div>
  );
}
